<template>
  <div class="tw-flex tw-flex-col tw-text-lg">
    <div class="tw-flex tw-items-center tw-gap-3">
      <AdvisorCoreCheckbox
        v-model="checkupStore.optimizerChecked"
        :is-active="isActive"
      />
      <div>
        <span class="tw-font-semibold">Compute Optimized Portfolio</span>
        <span class="tw-text-[#B3B9C6]"> (optional)</span>
        <StakeTooltip
          label="Using your selected risk tolerance and optional values, YourStake will minimize tracking error to an ACWI/AGG benchmark. More details will be provided after optimization on the 'Optimization' tab of AdvisorCore."
          position="right"
        >
          <i class="fa fa-question-circle tw-ml-1" />
        </StakeTooltip>
      </div>
    </div>
    <div
      v-if="checkupStore.optimizerChecked"
      class="tw-mt-4 tw-ml-4 tw-space-y-4"
    >
      <div v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')">
        <!-- <div> -->
        <div class="tw-mb-2">
          <label class="tw-block tw-mb-2 tw-font-medium">Tax Lot Sources</label>
          <p class="tw-text-xs tw-text-gray-500 tw-mb-2">
            Totals reflect values from Eclipse. When tax lots are fetched for optimization, they may vary slightly due to market conditions.
          </p>

          <!-- List of added tax lot entries -->
          <div
            v-if="checkupStore.orionTaxLots.length > 0"
            class="tw-mb-2 tw-space-y-1"
          >
            <div
              v-for="(entry, index) in checkupStore.orionTaxLots"
              :key="index"
              class="tw-flex tw-items-center tw-justify-between tw-bg-[#F6F7F8] tw-border tw-border-[#DFE2E7] tw-rounded-lg tw-px-3 tw-py-2"
            >
              <div class="tw-flex tw-items-center tw-gap-2">
                <i
                  :class="entry.type === 'orion' ? 'fa fa-university' : 'fa fa-briefcase'"
                  class="tw-text-gray-500"
                />
                <span class="tw-text-sm">{{ entry.name }}</span>
                <span
                  v-if="entry.accountId"
                  class="tw-text-xs tw-text-gray-500"
                >({{ entry.accountId }})</span>
                <span
                  v-if="entry.dollarValue != null"
                  class="tw-text-xs tw-text-gray-500"
                >${{ Number(entry.dollarValue).toLocaleString('en-US', { maximumFractionDigits: 0 }) }}</span>
                <span class="tw-text-xs tw-text-gray-400">({{ entry.type === 'orion' ? 'Orion' : 'Model Portfolio' }})</span>
                <span
                  v-if="entry.accountTypeName && entry.type !== 'model'"
                  class="tw-text-xs tw-text-gray-500 tw-bg-gray-200 tw-rounded tw-px-1"
                >{{ entry.accountTypeName }}</span>
                <select
                  v-if="entry.type === 'model'"
                  :value="entry.accountTypeName || ''"
                  class="tw-text-xs tw-text-gray-700 tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded tw-px-1 tw-py-0.5 tw-cursor-pointer"
                  @change="setModelAccountType(entry, $event.target.value)"
                >
                  <option value="">
                    Taxable
                  </option>
                  <option value="IRA">
                    IRA
                  </option>
                  <option value="Roth IRA">
                    Roth IRA
                  </option>
                  <option value="401k">
                    401(k)
                  </option>
                  <option value="403b">
                    403(b)
                  </option>
                  <option value="Rollover IRA">
                    Rollover IRA
                  </option>
                  <option value="SEP IRA">
                    SEP IRA
                  </option>
                  <option value="SIMPLE IRA">
                    SIMPLE IRA
                  </option>
                </select>
                <label
                  class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 tw-cursor-pointer tw-select-none"
                >
                  <input
                    type="checkbox"
                    :checked="entry.taxable !== false"
                    class="tw-cursor-pointer"
                    @change="entry.taxable = $event.target.checked"
                  >
                  Taxable
                </label>
                <button
                  v-if="entry.type === 'orion'"
                  class="tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-xs tw-text-blue-500 hover:tw-text-blue-700 tw-p-0"
                  title="Save Tax Lots as Portfolio"
                  @click="saveTaxLotsAsPortfolio(entry)"
                >
                  <i class="fa fa-floppy-o" />
                  Save as Portfolio
                </button>
              </div>
              <div class="tw-flex tw-items-center tw-gap-1">
                <button
                  class="tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-gray-400 hover:tw-text-red-500"
                  @click="removeTaxLot(index)"
                >
                  <i class="fa fa-times" />
                </button>
              </div>
            </div>
          </div>

          <!-- Add new entry UI -->
          <div
            v-if="addingType === 'orion'"
            class="tw-mb-2"
          >
            <OrionTaxLots
              :on-selected="addOrionTaxLot"
              :on-loading-exclusions="() => addingExclusionsLoading = true"
            />
            <button
              class="tw-mt-1 tw-text-sm tw-text-gray-400 tw-bg-transparent tw-border-none tw-cursor-pointer"
              @click="addingType = null"
            >
              Cancel
            </button>
          </div>

          <div
            v-else-if="addingType === 'model'"
            class="tw-mb-2"
          >
            <PortfolioSearchSelect
              placeholder="Search for model portfolio"
              @selected="addModelPortfolioTaxLot"
            />
            <button
              class="tw-mt-1 tw-text-sm tw-text-gray-400 tw-bg-transparent tw-border-none tw-cursor-pointer"
              @click="addingType = null"
            >
              Cancel
            </button>
          </div>

          <!-- Add buttons -->
          <div
            v-if="!addingType"
            class="tw-flex tw-gap-2"
          >
            <button
              class="tw-text-sm tw-bg-[#F6F7F8] tw-border tw-border-[#DFE2E7] tw-border-solid tw-rounded-lg tw-px-3 tw-py-1 tw-cursor-pointer hover:tw-bg-gray-200"
              @click="addingType = 'orion'"
            >
              <i class="fa fa-plus tw-mr-1" />Add Orion Account
            </button>
            <button
              class="tw-text-sm tw-bg-[#F6F7F8] tw-border tw-border-[#DFE2E7] tw-border-solid tw-rounded-lg tw-px-3 tw-py-1 tw-cursor-pointer hover:tw-bg-gray-200"
              @click="addingType = 'model'"
            >
              <i class="fa fa-plus tw-mr-1" />Add Model Portfolio
            </button>
          </div>

          <div
            v-if="addingExclusionsLoading"
            class="tw-text-sm tw-text-gray-500 tw-mt-1"
          >
            Loading Orion account details...
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-2 tw-gap-4">
          <div>
            <label class="tw-block tw-mb-2 tw-font-medium">Min Short Term Capital Gains</label>
            <input
              v-model="checkupStore.minSTCG"
              type="text"
              class="tw-w-full tw-p-2 focus:tw-outline-none tw-rounded-lg tw-bg-[#F6F7F8] tw-border tw-border-solid tw-border-[#DFE2E7] tw-text-base tw-text-gray-800"
            >
          </div>
          <div>
            <label class="tw-block tw-mb-2 tw-font-medium">Max Short Term Capital Gains</label>
            <input
              v-model="checkupStore.maxSTCG"
              type="text"
              class="tw-w-full tw-p-2 focus:tw-outline-none tw-rounded-lg tw-bg-[#F6F7F8] tw-border tw-border-solid tw-border-[#DFE2E7] tw-text-base tw-text-gray-800"
            >
          </div>
          <div>
            <label class="tw-block tw-mb-2 tw-font-medium">Min Long Term Capital Gains</label>
            <input
              v-model="checkupStore.minLTCG"
              type="text"
              class="tw-w-full tw-p-2 focus:tw-outline-none tw-rounded-lg tw-bg-[#F6F7F8] tw-border tw-border-solid tw-border-[#DFE2E7] tw-text-base tw-text-gray-800"
            >
          </div>
          <div>
            <label class="tw-block tw-mb-2 tw-font-medium">Max Long Term Capital Gains</label>
            <input
              v-model="checkupStore.maxLTCG"
              type="text"
              class="tw-w-full tw-p-2 focus:tw-outline-none tw-rounded-lg tw-bg-[#F6F7F8] tw-border tw-border-solid tw-border-[#DFE2E7] tw-text-base tw-text-gray-800"
            >
          </div>
        </div>
        <div>
          <label class="tw-block tw-mb-2 tw-mt-2 tw-font-medium">Cash Target for Transfer (USD)</label>
          <input
            v-model="checkupStore.cashTarget"
            type="text"
            class="tw-w-full tw-p-2 focus:tw-outline-none tw-rounded-lg tw-bg-[#F6F7F8] tw-border tw-border-solid tw-border-[#DFE2E7] tw-text-base tw-text-gray-800"
          >
        </div>
        <div class="tw-space-y-2 tw-mt-2">
          <div class="tw-flex tw-items-center tw-gap-2">
            <AdvisorCoreCheckbox
              v-model="checkupStore.harvestAllTaxLosses"
              :is-active="isActive"
            />
            <div><span class="tw-font-medium">Sell All Tax Losses</span></div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <AdvisorCoreCheckbox
              v-model="checkupStore.harvestOnlyTaxLosses"
              :is-active="isActive"
            />
            <div><span class="tw-font-medium">Sell Only Tax Losses</span></div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <AdvisorCoreCheckbox
              v-model="checkupStore.tryToSellExclusions"
              :is-active="isActive"
            />
            <div><span class="tw-font-medium">Try to Sell Exclusions</span></div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <AdvisorCoreCheckbox
              v-model="checkupStore.onlySell"
              :is-active="isActive"
            />
            <div><span class="tw-font-medium">Only Sell</span></div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <AdvisorCoreCheckbox
              v-model="checkupStore.onlyBuy"
              :is-active="isActive"
            />
            <div><span class="tw-font-medium">Only Buy</span></div>
          </div>
          <div
            v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax') && hasMixedTaxStatus"
            class="tw-flex tw-items-center tw-gap-2"
          >
            <AdvisorCoreCheckbox
              v-model="checkupStore.optimizeAssetLocation"
              :is-active="isActive"
            />
            <div>
              <span class="tw-font-medium">Optimize Asset Location</span>
              <StakeTooltip
                label="Recommends which account type to place each new purchase in for maximum tax efficiency. Only available when multiple accounts with different tax statuses are selected."
                position="right"
              >
                <i class="fa fa-question-circle tw-ml-1" />
              </StakeTooltip>
            </div>
          </div>
          <div
            v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax') && checkupStore.orionTaxLots.length > 0"
            class="tw-flex tw-items-center tw-gap-2"
          >
            <AdvisorCoreCheckbox
              :model-value="checkupStore.useWholeShares"
              :is-active="isActive"
              @update:model-value="checkupStore.setUseWholeShares($event)"
            />
            <div>
              <span class="tw-font-medium">Use Whole Shares</span>
              <StakeTooltip
                label="When enabled, the optimizer will round positions to whole shares. Recommended for custodians that do not support fractional shares."
                position="right"
              >
                <i class="fa fa-question-circle tw-ml-1" />
              </StakeTooltip>
            </div>
          </div>
          <div
            v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')"
            class="tw-flex tw-items-center tw-gap-2"
          >
            <AdvisorCoreCheckbox
              v-model="checkupStore.constrain_us_stock_exposure"
              :is-active="isActive"
            />
            <div>
              <span class="tw-font-medium">Constrain US Stock Exposure</span>
              <StakeTooltip
                label="When enabled, the optimizer will ensure US stock exposure is greater than or equal to the benchmark. When disabled, the optimizer can freely allocate between US and international stocks."
                position="right"
              >
                <i class="fa fa-question-circle tw-ml-1" />
              </StakeTooltip>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <AdvisorCoreCheckbox
              v-model="checkupStore.equityFocus"
              :is-active="isActive"
            />
            <div>
              <span class="tw-font-medium">Constrain Equity Allocation</span>
              <StakeTooltip
                label="When enabled, the optimizer will constrain the equity vs non-equity percentages to match the benchmark. When disabled, the optimizer can freely adjust the equity/bond allocation."
                position="right"
              >
                <i class="fa fa-question-circle tw-ml-1" />
              </StakeTooltip>
            </div>
          </div>
        </div>
      </div>
      <div class="tw-flex tw-gap-4">
        <div class="tw-flex-1">
          <label class="tw-block tw-mb-2 tw-font-medium">Include specific securities</label>
          <b-taginput
            v-model="checkupStore.specificInclusions"
            :data="filteredInclusionSymbols"
            :allow-new="false"
            autocomplete
            placeholder="e.g. NVDA"
            class="tw-w-full"
            @typing="filterInclusionSymbols"
            @paste.native="handlePaste($event, 'specificInclusions')"
          />
        </div>
        <div class="tw-flex-1">
          <label class="tw-block tw-mb-2 tw-font-medium">Exclude specific securities</label>
          <b-taginput
            v-model="checkupStore.specificExclusions"
            :data="filteredExclusionSymbols"
            :allow-new="false"
            autocomplete
            placeholder="e.g. NVDA"
            class="tw-w-full"
            @typing="filterExclusionSymbols"
            @paste.native="handlePaste($event, 'specificExclusions')"
          />
        </div>
      </div>
      <div v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')">
        <!-- <div> -->
        <div v-if="!addingExclusionsLoading">
          <div class="tw-flex tw-gap-4">
            <div class="tw-flex-1">
              <label class="tw-block tw-mb-2 tw-font-medium">Do not Buy Securities</label>
              <b-taginput
                v-model="checkupStore.specificDoNotBuy"
                :data="filteredDoNotBuySellSymbols"
                :allow-new="false"
                autocomplete
                placeholder="e.g. NVDA"
                class="tw-w-full"
                @typing="filterDoNotBuySellSymbols"
                @paste.native="handlePaste($event, 'specificDoNotBuy')"
              />
            </div>
            <div class="tw-flex-1">
              <label class="tw-block tw-mb-2 tw-font-medium">Do not Sell Securities</label>
              <b-taginput
                v-model="checkupStore.specificDoNotSell"
                :data="filteredDoNotBuySellSymbols"
                :allow-new="false"
                autocomplete
                placeholder="e.g. NVDA"
                class="tw-w-full"
                @typing="filterDoNotBuySellSymbols"
                @paste.native="handlePaste($event, 'specificDoNotSell')"
              />
            </div>
          </div>
          <div class="tw-mt-4">
            <label class="tw-block tw-mb-2 tw-font-medium">Ignore Securities in Optimization</label>
            <b-taginput
              v-model="checkupStore.ignoredSecurities"
              :data="filteredDoNotBuySellSymbols"
              :allow-new="false"
              autocomplete
              placeholder="e.g. NVDA"
              class="tw-w-full"
              @typing="filterDoNotBuySellSymbols"
              @paste.native="handlePaste($event, 'ignoredSecurities')"
            />
          </div>
        </div>
        <div v-else>
          Loading exclusions from Orion...
        </div>
      </div>

      <AdvancedTaxSettings v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')" />
      <SymbolProxySettings v-if="checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')" />
    </div>
  </div>
</template>


<script>
import AdvisorCoreCheckbox from '@frontend/ui/components/utils/AdvisorCoreCheckbox.vue';
import AdvancedTaxSettings from '@frontend/ui/features/checkup/components/portfolio-select/AdvancedTaxSettings.vue';
import SymbolProxySettings from '@frontend/ui/features/checkup/components/portfolio-select/SymbolProxySettings.vue';
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import OrionTaxLots from '../../../../../dash3/js/components/integrations/orion/OrionTaxLots.vue';
import PortfolioSearchSelect from '@frontend/ui/features/checkup/components/portfolio-select/PortfolioSearchSelect.vue';
import StakeTooltip from '@stakeui/StakeTooltip.vue';

export default {
  components: {
    AdvisorCoreCheckbox,
    AdvancedTaxSettings,
    SymbolProxySettings,
    OrionTaxLots,
    PortfolioSearchSelect,
    StakeTooltip,
  },
  props: {
    isActive: { type: Boolean, required: true },
  },
  data() {
    return {
      filteredInclusionSymbols: [],
      filteredExclusionSymbols: [],
      filteredDoNotBuySellSymbols: [],
      addingType: null, // null | 'orion' | 'model'
      addingExclusionsLoading: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    hasMixedTaxStatus() {
      const lots = this.checkupStore.orionTaxLots;
      if (lots.length < 2) return false;
      return lots.some(t => t.taxable === false) && lots.some(t => t.taxable !== false);
    },
    // Bond CUSIPs from Orion tax lots (restrictions and unresolved symbols) may not be
    // in the equity symbol list, so merge them in so they are searchable in DNB/DNS.
    // Computed once per orionTaxLots/availableSymbols change, not on every keystroke.
    dnbSymbolPool() {
      const taxLotSymbols = this.checkupStore.orionTaxLots.flatMap(t => [
        ...(t.exclusions || []),
        ...(t.missingSymbols || []),
      ]);
      const proxiedOriginals = (this.checkupStore.symbolProxies || []).map(p => p.originalSymbol).filter(Boolean);
      return [...new Set([...this.checkupStore.availableSymbols, ...taxLotSymbols, ...proxiedOriginals])];
    },
  },
  watch: {
    hasMixedTaxStatus(newVal) {
      if (!newVal) this.checkupStore.optimizeAssetLocation = false;
    },
  },
  methods: {
    setModelAccountType(entry, typeName) {
      entry.accountTypeName = typeName || null;
      entry.taxable = !typeName;
    },
    async addOrionTaxLot(portfolio, symbolList, missingSymbols = []) {
      this.addingExclusionsLoading = false;
      this.addingType = null;

      const NON_TAXABLE_ACCOUNT_TYPES = new Set([
        'IRA', 'ROTH', 'ROTHC', 'ROTHV', 'ROLL', 'SEP', 'SEPI', 'SIMP',
        'IRC', 'INH', 'BIRA', 'RP', '401K', '403B', 'INHROTH',
        'APEX ROTH_', 'APEX TRADI', 'Roth I401k', 'I401(k)',
      ]);
      const accountTypeName = (portfolio.accountTypeName || '').trim();
      const isTaxable = !NON_TAXABLE_ACCOUNT_TYPES.has(accountTypeName);

      this.checkupStore.orionTaxLots.push({
        type: 'orion',
        portfolioId: portfolio.portfolioId,
        accountId: portfolio.accountId || null,
        name: portfolio.accountName || portfolio.nickname || portfolio.portfolioId,
        dollarValue: portfolio.value != null ? portfolio.value : null,
        exclusions: portfolio.exclusions || [],
        missingSymbols: missingSymbols || [],
        entityId: null,
        taxable: isTaxable,
        custodian: portfolio.custodian || null,
        accountTypeName: accountTypeName || null,
      });

      // Auto-enable whole shares for custodians that don't support fractional shares (e.g. Schwab).
      // Only set if the user hasn't explicitly overridden the checkbox.
      const isApex = portfolio.custodian && portfolio.custodian.toLowerCase().includes('apex');
      if (portfolio.custodian && !isApex && this.checkupStore.useWholeSharesExplicit === null) {
        this.checkupStore.useWholeShares = true;
      }

      // Merge exclusions from all Orion accounts
      const allExclusions = this.checkupStore.orionTaxLots
          .filter(t => t.type === 'orion')
          .flatMap(t => t.exclusions || []);
      this.checkupStore.specificDoNotBuy = [...new Set(allExclusions)];
      this.checkupStore.specificDoNotSell = [...new Set(allExclusions)];

      this.checkupStore.unresolvedTaxLotSymbols = [
        ...new Set(this.checkupStore.orionTaxLots.flatMap(t => t.missingSymbols || [])),
      ];

      const currentSymbols = new Set(this.checkupStore.availableSymbols);
      for (const symbol of symbolList) {
        if (!currentSymbols.has(symbol)) {
          this.checkupStore.availableSymbols.push(symbol);
        }
      }
      this.checkupStore.availableSymbols.sort((x, y) => {
        if (x.length !== y.length) return x.length - y.length;
        if (x < y) return -1;
        if (x > y) return 1;
        return 0;
      });
    },
    async addModelPortfolioTaxLot(portfolio) {
      this.addingType = null;
      const entityId = portfolio.external_id || portfolio.id;

      let missingSymbols = [];
      if (this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')) {
        const res = await fetch(`/api/v1/model_portfolios/${entityId}/validate_tax_lots/`, {
          headers: { 'X-CSRFToken': this.$root.$data.csrfToken },
        });
        const data = await res.json();
        if (data.missing_purchase_dates?.length > 0) {
          const symbols = data.missing_purchase_dates.join(', ');
          this.$buefy.dialog.alert({
            type: 'is-danger',
            title: 'Missing Purchase Dates',
            message: `The following holdings in <strong>${portfolio.name}</strong> are missing a purchase date: <strong>${symbols}</strong>.<br><br>Please re-upload the model portfolio with a purchase date for every holding so that long-term vs. short-term capital gains can be determined correctly.`,
          });
          return;
        }
        missingSymbols = data.unmatched_symbols || [];
      }

      this.checkupStore.orionTaxLots.push({
        type: 'model',
        portfolioId: null,
        name: portfolio.name,
        exclusions: [],
        entityId,
        taxable: true,
        accountTypeName: null,
        missingSymbols,
      });

      this.checkupStore.unresolvedTaxLotSymbols = [
        ...new Set(this.checkupStore.orionTaxLots.flatMap(t => t.missingSymbols || [])),
      ];
    },
    async saveTaxLotsAsPortfolio(entry) {
      const defaultName = `${entry.name} Tax Lots`;
      this.$buefy.dialog.prompt({
        message: 'Enter a name for the portfolio',
        inputAttrs: {
          maxlength: 250,
          value: defaultName,
        },
        confirmText: 'Save',
        cancelText: 'Cancel',
        onConfirm: async portfolioName => {
          try {
            const response = await fetch('/api/v1/integrations/orion/eclipse/tax_lots/save_tax_lots_as_portfolio/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.$root.$data.csrfToken,
              },
              body: JSON.stringify({
                orion_account_id: entry.portfolioId,
                portfolio_name: portfolioName || defaultName,
              }),
            });
            if (response.ok) {
              const result = await response.json();
              this.$buefy.toast.open({
                message: `Portfolio "${result.portfolio_name}" created successfully!`,
                type: 'is-success',
              });
            } else {
              throw new Error('Failed to save portfolio');
            }
          } catch (error) {
            this.$buefy.toast.open({
              message: 'Error saving portfolio: ' + error.message,
              type: 'is-danger',
            });
          }
        },
      });
    },
    removeTaxLot(index) {
      this.checkupStore.orionTaxLots.splice(index, 1);

      // Recompute merged exclusions after removal
      const allExclusions = this.checkupStore.orionTaxLots
          .filter(t => t.type === 'orion')
          .flatMap(t => t.exclusions || []);
      this.checkupStore.specificDoNotBuy = [...new Set(allExclusions)];
      this.checkupStore.specificDoNotSell = [...new Set(allExclusions)];

      // Recompute unresolved symbols from remaining Orion lots only
      const remainingMissing = this.checkupStore.orionTaxLots
          .filter(t => t.type === 'orion')
          .flatMap(t => t.missingSymbols || []);
      this.checkupStore.unresolvedTaxLotSymbols = [...new Set(remainingMissing)];
    },
    filterInclusionSymbols(value) {
      this.filteredInclusionSymbols = this.checkupStore.availableSymbols.filter(symbol => symbol.toUpperCase().includes(value.toUpperCase()));
    },
    filterExclusionSymbols(value) {
      this.filteredExclusionSymbols = this.checkupStore.availableSymbols.filter(symbol => symbol.toUpperCase().includes(value.toUpperCase()));
    },
    filterDoNotBuySellSymbols(value) {
      const upper = value.toUpperCase();
      this.filteredDoNotBuySellSymbols = this.dnbSymbolPool.filter(
          symbol => symbol && symbol.toUpperCase().includes(upper),
      );
      this.filteredDoNotBuySellSymbols.sort((x, y) => {
        if (x.length !== y.length) return x.length - y.length;
        if (x < y) return -1;
        if (x > y) return 1;
        return 0;
      });
    },
    handlePaste(event, fieldName) {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');

      // Split by commas, newlines, tabs, or semicolons
      const symbols = pastedText
          .split(/[\n,;\t]+/)
          .map(s => s.trim().toUpperCase())
          .filter(s => s.length > 0);

      // Get current values
      const currentValues = this.checkupStore[fieldName] || [];

      // Separate valid and invalid symbols
      const validSymbols = [];
      const invalidSymbols = [];

      // DNB/DNS/ignored fields allow bonds and securities not in our system
      // (from Orion or model portfolio tax lots), so validate against the
      // broader dnbSymbolPool instead of just availableSymbols.
      const dnbFields = new Set(['specificDoNotBuy', 'specificDoNotSell', 'ignoredSecurities']);
      const symbolPool = dnbFields.has(fieldName) ? this.dnbSymbolPool : this.checkupStore.availableSymbols;

      symbols.forEach(symbol => {
        const isValid = symbolPool.some(available =>
          available.toUpperCase() === symbol,
        );

        if (isValid && !currentValues.includes(symbol)) {
          validSymbols.push(symbol);
        } else if (!isValid) {
          invalidSymbols.push(symbol);
        }
      });

      // Update the store with valid symbols
      if (validSymbols.length > 0) {
        this.checkupStore[fieldName] = [...currentValues, ...validSymbols];
      }

      // Show error message for invalid symbols
      if (invalidSymbols.length > 0) {
        const symbolList = invalidSymbols.join(', ');
        const message = invalidSymbols.length === 1 ?
          `Symbol not found: ${symbolList}` :
          `Symbols not found: ${symbolList}`;

        this.$buefy.toast.open({
          message,
          type: 'is-danger',
          duration: 5000,
          position: 'is-top',
        });
      }
    },
  },
};
</script>


  <style scoped>

  </style>
